if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();

      this.sliderInstance = false;
      this.lastWheelTime = 0;
      this.wheelDelay = 450;

      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = Number.parseInt(this.dataset.items, 10) || 1;
      this.tabletItems = Number.parseInt(this.dataset.tabletItems, 10) || 1;
      this.mobileGap = Number.parseInt(this.dataset.mobileGap, 10) || 28;
      this.slideCount = this.sliderWrapper?.children.length || 0;

      if (!this.enableSlider || !this.sliderWrapper || this.slideCount < 2) {
        return;
      }

      this.initSlider();

      document.addEventListener('matchMobile', this.handleViewportChange);
      document.addEventListener('unmatchMobile', this.handleViewportChange);
      this.addEventListener('wheel', this.handleWheel, { passive: false });

      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('matchMobile', this.handleViewportChange);
      document.removeEventListener('unmatchMobile', this.handleViewportChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.removeEventListener('wheel', this.handleWheel);

      this.destroySlider();
    }

    handleViewportChange() {
      this.destroySlider();

      requestAnimationFrame(() => {
        this.initSlider();
      });
    }

    initSlider() {
      if (
        !this.enableSlider ||
        !this.sliderWrapper ||
        this.slideCount < 2 ||
        typeof this.sliderInstance === 'object'
      ) {
        return;
      }

      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);
      const tabletSlides = this.tabletItems > 3 ? 3 : this.tabletItems;
      const maximumVisibleSlides = Math.max(this.items, tabletSlides);
      const isMobile = window.matchMedia('(max-width: 767.98px)').matches;

      /*
       * Desktop/tablet peek:
       * If there are more cards than the selected column count,
       * show the selected number of full cards + part of the next card.
       *
       * Example:
       * 2 columns + 3 cards => 2 full cards + a peek of card 3.
       *
       * If all cards already fit, the Liquid section applies the
       * collection-list--fits-desktop grid state instead.
       */
      const peekAmount = 0.50;

      const desktopSlides =
        this.slideCount > this.items
          ? Math.min(this.items + peekAmount, this.slideCount)
          : this.items;

      const tabletSlidesWithPeek =
        this.slideCount > tabletSlides
          ? Math.min(tabletSlides + peekAmount, this.slideCount)
          : tabletSlides;

      /*
       * Avoid looping when there is only one extra card.
       * This keeps a 2-column / 3-card carousel clean and prevents
       * duplicate slides from appearing in the peek.
       */
      const shouldLoop =
        !isMobile &&
        this.slideCount > maximumVisibleSlides + 1;

      const nextElement = this.section?.querySelector(this.selectors.nextEl);
      const previousElement = this.section?.querySelector(this.selectors.prevEl);

      const sliderOptions = {
        slidesPerView: 'auto',
        spaceBetween: this.mobileGap,
        allowTouchMove: true,
        simulateTouch: true,
        followFinger: true,
        shortSwipes: true,
        longSwipes: true,
        touchRatio: 1,
        resistanceRatio: 0.85,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        loop: shouldLoop,
        rewind: false,
        threshold: 2,
        pagination: false,
        breakpoints: {
          768: {
            slidesPerView: tabletSlidesWithPeek,
            spaceBetween: columnGap.tablet,
          },
          1024: {
            slidesPerView: desktopSlides,
            spaceBetween: columnGap.tabletLarge,
          },
          1280: {
            slidesPerView: desktopSlides,
            spaceBetween: columnGap.desktop,
          },
        },
      };

      if (nextElement && previousElement) {
        sliderOptions.navigation = {
          nextEl: nextElement,
          prevEl: previousElement,
        };
      }

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.grid);
      this.sliderWrapper.classList.add(this.classes.swiperWrapper);

      this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
      this.sliderInstance.init();

      const focusableElements = FoxTheme.a11y.getFocusableElements(this);

      focusableElements.forEach((element) => {
        element.addEventListener('focusin', () => {
          const slide = element.closest('.swiper-slide');
          const slider = this.sliderInstance?.slider;

          if (!slide || !slider) return;

          const slideIndex = Array.from(slider.slides).indexOf(slide);

          if (slideIndex >= 0) {
            slider.slideTo(slideIndex);
          }
        });
      });
    }

    handleWheel(event) {
      if (!window.matchMedia('(max-width: 767.98px)').matches) return;

      const slider = this.sliderInstance?.slider;

      if (!slider || slider.destroyed || slider.slides.length < 2) return;

      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;

      if (Math.abs(delta) < 6) return;

      const goingForward = delta > 0;
      const isLooping = Boolean(slider.params.loop);

      if (!isLooping) {
        if (goingForward && slider.isEnd) return;
        if (!goingForward && slider.isBeginning) return;
      }

      event.preventDefault();

      const currentTime = Date.now();

      if (currentTime - this.lastWheelTime < this.wheelDelay) return;

      this.lastWheelTime = currentTime;

      if (goingForward) {
        slider.slideNext();
      } else {
        slider.slidePrev();
      }
    }

    handleBlockSelect(event) {
      if (event.detail.sectionId !== this.sectionId) return;

      const slider = this.sliderInstance?.slider;
      const index = Number(event.target.dataset.index);

      if (!slider || Number.isNaN(index)) return;

      if (slider.params.loop && typeof slider.slideToLoop === 'function') {
        slider.slideToLoop(index);
      } else {
        slider.slideTo(index);
      }
    }

    destroySlider() {
      this.classList.remove(this.classes?.swiper);
      this.sliderWrapper?.classList.remove(this.classes?.swiperWrapper);
      this.sliderWrapper?.classList.add(this.classes?.grid);

      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider?.destroy(true, true);
        this.sliderInstance = false;
      }
    }
  }

  customElements.define('collection-list', CollectionList);
}